import React from 'react';

import type { AiChatReference } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import ViewCommunicationCardContainer from '~/components/communications/ViewCommunicationCardContainer';
import Chip from '~/components/core/Atomic/Chip/Chip';
import PreviewCard from '~/components/ui/atoms/PreviewCard';

import { NoteReferenceDialog } from '../NoteReferenceDIalog';

import { DocumentReferencePreview } from './DocumentReferencePreview';

/**
 * DocumentReferenceTrigger
 *
 * Handles document/note/communication references.
 * Manages click interactions to open full detail dialogs.
 */

interface DocumentReferenceTriggerProps {
  reference: AiChatReference;
  children: React.ReactNode;
}

export const DocumentReferenceTrigger: React.FC<DocumentReferenceTriggerProps> = ({ reference, children }) => {
  const { claim } = useClaim({ claimRequired: true });
  const [selectedReference, setSelectedReference] = React.useState<AiChatReference | undefined>();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    // For documents, open directly in a new tab
    if (reference.type === 'document') {
      const documentUrl = `/api/v1/claims/${claim.id}/documents/${reference.id}`;
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // For other types (communication, note), show the dialog
    setSelectedReference(reference);
  };

  const handleCloseDialog = () => {
    setSelectedReference(undefined);
  };

  const trigger = (
    <Chip
      color="primary"
      onClick={handleClick}
      label={<span className="block max-w-[114px] truncate text-xs font-semibold">{children}</span>}
      size="small"
      className="h-20 max-w-[114px] bg-azure-200 text-azure-700 hover:bg-azure-200 hover:text-azure-700"
    />
  );

  return (
    <>
      <PreviewCard trigger={trigger}>
        <DocumentReferencePreview reference={reference} />
      </PreviewCard>

      {selectedReference?.type === 'communication' && (
        <ViewCommunicationCardContainer
          isDialog
          key={selectedReference.id}
          communicationId={Number(selectedReference.id)}
          onClose={handleCloseDialog}
          disableAdditionalActions
        />
      )}
      {selectedReference?.type === 'note' && (
        <NoteReferenceDialog noteReference={selectedReference} onClose={handleCloseDialog} claimId={claim.id} />
      )}
    </>
  );
};
